/**
 * The arithmetic the dashboard draws from, kept out of the component so it can be tested without
 * a timer or a mounted page: the counts folded into a fixed-order map, whether the batch has
 * stopped moving, and whether the worker looks paused.
 */
import {
  ACTIVE_JOB_STATUSES,
  JOB_STATUSES,
  type BatchSummary,
  type JobStatus,
  type StatusCount,
} from './importApi';

export type StatusCountMap = Record<JobStatus, number>;

/** Zero for every status, so a chip renders 0 rather than disappearing when its status is absent. */
export function emptyStatusMap(): StatusCountMap {
  return JOB_STATUSES.reduce((acc, s) => {
    acc[s] = 0;
    return acc;
  }, {} as StatusCountMap);
}

/**
 * Fold the backend's `[{status, count}]` into the fixed-order map the summary shows.
 *
 * Unknown statuses are ignored rather than added: the status set is a closed enum the worker owns,
 * and a stray value is a backend bug that should surface in its own tests, not as a mystery chip.
 */
export function summariseCounts(counts: StatusCount[] | undefined | null): StatusCountMap {
  const map = emptyStatusMap();
  for (const row of counts ?? []) {
    const key = String(row?.status ?? '').toLowerCase() as JobStatus;
    if (key in map) map[key] += Number(row?.count) || 0;
  }
  return map;
}

/** Jobs still queued or running. */
export function activeCount(map: StatusCountMap): number {
  return ACTIVE_JOB_STATUSES.reduce((n, s) => n + (map[s] || 0), 0);
}

export function totalCount(map: StatusCountMap): number {
  return JOB_STATUSES.reduce((n, s) => n + (map[s] || 0), 0);
}

/**
 * A batch is terminal when nothing is queued or running: polling can stop.
 *
 * It needs at least one job to be terminal, so a summary read before the first file stages (every
 * count zero) is not mistaken for a finished batch and does not stop the first poll.
 */
export function isTerminal(map: StatusCountMap): boolean {
  return totalCount(map) > 0 && activeCount(map) === 0;
}

/**
 * How many consecutive idle polls before the worker is called paused rather than merely waiting.
 *
 * At a 4s poll this is about twelve seconds of queued-but-nothing-running, which a healthy worker
 * clears in one or two polls as it picks up the next file. Longer than that, with the budget cap
 * being the one thing that holds a queue without failing it, reads as paused.
 */
export const PAUSE_IDLE_POLLS = 3;

export type WorkerActivity = 'running' | 'waiting' | 'paused' | 'idle';

/**
 * What the worker looks like it is doing, from the counts and how long it has looked idle.
 *
 * `running` while anything runs. With a queue but nothing running it is `waiting` at first and
 * `paused` once it has stayed that way for `PAUSE_IDLE_POLLS` — the summary carries no explicit
 * paused flag, so this is inferred, and the copy the banner shows says "may be" accordingly. With
 * no queue and nothing running it is `idle` (terminal or empty).
 */
export function workerActivity(map: StatusCountMap, consecutiveIdlePolls: number): WorkerActivity {
  if ((map.running || 0) > 0) return 'running';
  if ((map.queued || 0) > 0) {
    return consecutiveIdlePolls >= PAUSE_IDLE_POLLS ? 'paused' : 'waiting';
  }
  return 'idle';
}

/**
 * Advance the consecutive-idle-poll counter for the next summary.
 *
 * It climbs only while the queue is held with nothing running, and resets the moment anything runs
 * or a job finishes — a worker that is making progress, however slowly, is not paused. Pulled out
 * so the "when does the banner appear" rule is one tested function rather than scattered state.
 */
export function nextIdlePolls(
  prev: StatusCountMap,
  curr: StatusCountMap,
  priorCount: number,
): number {
  const stalled = (curr.running || 0) === 0 && (curr.queued || 0) > 0;
  if (!stalled) return 0;
  // Any job leaving the queue (done/failed/etc. rising, or queued falling) is progress.
  const madeProgress =
    totalCount(curr) - activeCount(curr) > totalCount(prev) - activeCount(prev) ||
    (curr.queued || 0) < (prev.queued || 0);
  return madeProgress ? 0 : priorCount + 1;
}

/** Summary -> the map, for callers that have the raw response. */
export function mapFromSummary(summary: BatchSummary | null | undefined): StatusCountMap {
  return summariseCounts(summary?.counts);
}

/** Split a list into chunks of at most `size`, for grouping an over-twenty file pick. */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) throw new Error('chunk size must be positive');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** A cost in dollars at the precision it deserves; mirrors the editor's `formatCost`. */
export function formatCost(usd: number | null | undefined): string {
  if (typeof usd !== 'number' || !Number.isFinite(usd)) return '$0';
  if (usd === 0) return '$0';
  return `$${usd.toFixed(usd < 0.1 ? 4 : 2)}`;
}
